import { useEffect, useState } from 'react'
import { RoomStateEvent } from 'matrix-js-sdk'
import { requireClient } from '@/lib/matrix/client'
import { useMemberStore, selectMembers, selectPresence, type MemberData } from '@/lib/store/member-store'
import { canInvite, userPowerLevel } from '@/lib/matrix/membership'
import { Avatar } from '@/components/ui/Avatar'
import { UserProfileModal } from './UserProfileModal'
import { InvitePeopleDialog } from './InvitePeopleDialog'

interface MemberListPanelProps {
  roomId: string
  onClose: () => void
}

export function MemberListPanel({ roomId, onClose }: MemberListPanelProps) {
  const members = useMemberStore(selectMembers(roomId))
  const setMembers = useMemberStore((s) => s.setMembers)
  const [profileUserId, setProfileUserId] = useState<string | null>(null)
  const [showInvite, setShowInvite] = useState(false)

  const client = requireClient()
  const room = client.getRoom(roomId)
  const mayInvite = !!room && canInvite(client, room)
  const roomName = room?.name ?? ''

  useEffect(() => {
    const room = requireClient().getRoom(roomId)
    if (!room) return

    // Recompute from the live room so the list reflects membership and role
    // changes (invites, kicks, bans, promotions) without reopening the panel.
    const recompute = () => {
      const all = room.getMembers()
        .filter((m) => m.membership === 'join' || m.membership === 'invite' || m.membership === 'ban')
        .map((m): MemberData => ({
          userId: m.userId,
          displayName: m.name || m.userId,
          avatarUrl: m.getMxcAvatarUrl() ?? null,
          membership: m.membership ?? 'join',
          powerLevel: userPowerLevel(room, m.userId),
        }))
      all.sort((a, b) => b.powerLevel - a.powerLevel || a.displayName.localeCompare(b.displayName))
      setMembers(roomId, all)
    }

    recompute()
    room.currentState.on(RoomStateEvent.Update, recompute)
    return () => { room.currentState.off(RoomStateEvent.Update, recompute) }
  }, [roomId, setMembers])

  const joined = members.filter((m) => m.membership === 'join')
  const admins = joined.filter((m) => m.powerLevel >= 50)
  const regular = joined.filter((m) => m.powerLevel < 50)
  const invited = members.filter((m) => m.membership === 'invite')
  const banned = members.filter((m) => m.membership === 'ban')

  return (
    <aside className="members-panel">
      <div className="members-head">
        <span className="label-section">Members · {joined.length}</span>
        <div className="flex items-center gap-0.5">
          {mayInvite && (
            <button onClick={() => setShowInvite(true)} className="icon-btn-sm" title="Invite people">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
              </svg>
            </button>
          )}
          <button onClick={onClose} className="icon-btn-sm" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
      </div>
      <div className="scroll flex-1 overflow-y-auto" style={{ padding: '4px 8px 12px' }}>
        {admins.length > 0 && (
          <>
            <div className="label-section" style={{ padding: '8px 8px 4px' }}>Admins — {admins.length}</div>
            {admins.map((m) => <MemberRow key={m.userId} member={m} onClick={() => setProfileUserId(m.userId)} />)}
          </>
        )}
        <div className="label-section" style={{ padding: '12px 8px 4px' }}>Members — {regular.length}</div>
        {regular.map((m) => <MemberRow key={m.userId} member={m} onClick={() => setProfileUserId(m.userId)} />)}
        {invited.length > 0 && (
          <>
            <div className="label-section" style={{ padding: '12px 8px 4px' }}>Invited — {invited.length}</div>
            {invited.map((m) => <MemberRow key={m.userId} member={m} muted onClick={() => setProfileUserId(m.userId)} />)}
          </>
        )}
        {banned.length > 0 && (
          <>
            <div className="label-section" style={{ padding: '12px 8px 4px' }}>Banned — {banned.length}</div>
            {banned.map((m) => <MemberRow key={m.userId} member={m} muted onClick={() => setProfileUserId(m.userId)} />)}
          </>
        )}
      </div>
      {profileUserId && (
        <UserProfileModal userId={profileUserId} roomId={roomId} onClose={() => setProfileUserId(null)} />
      )}
      {showInvite && (
        <InvitePeopleDialog roomId={roomId} roomName={roomName} onClose={() => setShowInvite(false)} />
      )}
    </aside>
  )
}

function MemberRow({ member, onClick, muted }: { member: MemberData; onClick: () => void; muted?: boolean }) {
  const presence = useMemberStore(selectPresence(member.userId))
  const online = !muted && presence?.presence === 'online'
  const sub = member.membership === 'invite' ? 'Invited'
    : member.membership === 'ban' ? 'Banned'
    : online ? 'Online' : 'Offline'
  return (
    <button onClick={onClick} className={`member-row w-full text-left${muted ? ' opacity-60' : ''}`}>
      <div className="relative shrink-0">
        <Avatar mxc={member.avatarUrl} name={member.displayName} userId={member.userId} size={34} />
        {online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[rgb(var(--np-status-ok))] ring-2 ring-[rgb(var(--np-surface))]" />}
      </div>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-[rgb(var(--np-tx-primary))] truncate">{member.displayName}</span>
        <span className={`block text-[11px] ${online ? 'text-[rgb(var(--np-status-ok))]' : 'text-[rgb(var(--np-tx-muted))]'}`}>
          {sub}
        </span>
      </span>
    </button>
  )
}
